import { memo, useCallback, useEffect, useState } from 'react'
import { ChatInput } from '../components/ChatInput'
import { useChatContextOptional, useTurnContextOptional } from '../contexts'
import type { ChatMode } from '@/types/message'
import type { Character } from '@/types'
import type { Member as MemberType } from '@/types/member'
import type { Player, TurnPermissions } from '@/types/turn'

// Referencia estavel para "nenhum modulo ativo" (nao invalida o memo do ChatInput)
const EMPTY_MODULES: string[] = []

interface ChatInputContainerProps {
  // Props opcionais - se nao fornecidas, usa contexto
  chatMode?: ChatMode

  // Estado de input
  // (messageInput/privateMessageInput/onMessageInputChange/
  // onPrivateMessageInputChange saíram — Fase 11: o texto digitado é estado
  // local deste componente; o envio entrega o texto via onSendMessage(text))
  selectedCharacter?: Character | null
  selectedMediaFile?: string | null
  selectedMediaData?: { id: string; url: string; filename: string; type: string; size: number } | null

  // Handlers
  onSendMessage?: (text: string) => boolean
  onSendPrivateMessage?: (text: string) => Promise<boolean>
  onMediaUpload?: (mediaFile: { id: string; url: string; filename: string; type: string; size: number }) => void
  onSelectCharacter?: (character: Character | null) => void
  onRollDice?: () => void

  // Props adicionais
  disabled?: boolean
  isLoading?: boolean
  isConnected?: boolean
  loadingHistory?: boolean
  loadingPrivateHistory?: boolean
  selectedPrivateUser?: MemberType | null
  currentUserId?: string
  tableId?: string

  // Props do sistema de turnos
  permissions?: TurnPermissions
  currentPlayer?: Player | null
  isActive?: boolean
  isMaster?: boolean

  // Props para NPCs
  characters?: Character[]
  members?: MemberType[]
  respondingAsNpc?: {
    isActive: boolean
    npcUserId: string | null
    npcName: string | null
  }
  onToggleRespondAsNpc?: (npc: Character | null) => void
  onLiveSync?: () => void
  onPoll?: () => void
  onLab?: () => void
  // Foco do textarea (mobile esconde a BottomNavBar enquanto digita)
  onFocusChange?: (focused: boolean) => void
}

// Custom comparison function for React.memo
function arePropsEqual(prev: ChatInputContainerProps, next: ChatInputContainerProps): boolean {
  if (prev.chatMode !== next.chatMode) return false
  if (prev.onFocusChange !== next.onFocusChange) return false

  // State flags
  if (prev.disabled !== next.disabled) return false
  if (prev.isLoading !== next.isLoading) return false
  if (prev.isConnected !== next.isConnected) return false
  if (prev.loadingHistory !== next.loadingHistory) return false
  if (prev.loadingPrivateHistory !== next.loadingPrivateHistory) return false
  if (prev.isMaster !== next.isMaster) return false
  if (prev.isActive !== next.isActive) return false

  // IDs
  if (prev.currentUserId !== next.currentUserId) return false
  if (prev.tableId !== next.tableId) return false

  // Selected entities
  if (prev.selectedCharacter?.id !== next.selectedCharacter?.id) return false
  if (prev.selectedPrivateUser?.userId !== next.selectedPrivateUser?.userId) return false
  if (prev.selectedMediaFile !== next.selectedMediaFile) return false

  // Permissions
  if (prev.permissions?.canSendMessage !== next.permissions?.canSendMessage) return false
  if (prev.permissions?.isMyTurn !== next.permissions?.isMyTurn) return false

  // CurrentPlayer
  if (prev.currentPlayer?.id !== next.currentPlayer?.id) return false

  // respondingAsNpc
  if (prev.respondingAsNpc?.isActive !== next.respondingAsNpc?.isActive) return false
  if (prev.respondingAsNpc?.npcUserId !== next.respondingAsNpc?.npcUserId) return false

  // Arrays by reference
  if (prev.characters !== next.characters) return false
  if (prev.members !== next.members) return false

  // Callbacks by reference
  if (prev.onSendMessage !== next.onSendMessage) return false
  if (prev.onSendPrivateMessage !== next.onSendPrivateMessage) return false
  if (prev.onMediaUpload !== next.onMediaUpload) return false
  if (prev.onSelectCharacter !== next.onSelectCharacter) return false
  if (prev.onRollDice !== next.onRollDice) return false
  if (prev.onToggleRespondAsNpc !== next.onToggleRespondAsNpc) return false
  if (prev.onLiveSync !== next.onLiveSync) return false
  if (prev.onPoll !== next.onPoll) return false
  if (prev.onLab !== next.onLab) return false

  return true
}

export const ChatInputContainer = memo(function ChatInputContainer(props: ChatInputContainerProps) {
  // Tentar usar contextos se disponiveis
  const chatContext = useChatContextOptional()
  const turnContext = useTurnContextOptional()

  // Usar props se fornecidas, senao usar contexto
  const chatMode = props.chatMode ?? chatContext?.chatMode ?? 'group'
  const selectedCharacter = props.selectedCharacter !== undefined ? props.selectedCharacter : chatContext?.selectedCharacter
  const selectedMediaFile = props.selectedMediaFile !== undefined ? props.selectedMediaFile : chatContext?.selectedMediaFile
  const selectedMediaData = props.selectedMediaData !== undefined ? props.selectedMediaData : chatContext?.selectedMediaData
  const onSendMessage = props.onSendMessage ?? chatContext?.onSendMessage
  const onSendPrivateMessage = props.onSendPrivateMessage ?? chatContext?.onSendPrivateMessage
  const onMediaUpload = props.onMediaUpload ?? chatContext?.onMediaUpload
  const onSelectCharacter = props.onSelectCharacter ?? chatContext?.onSelectCharacter
  const onRollDice = props.onRollDice ?? chatContext?.onRollDice
  const isConnected = props.isConnected ?? chatContext?.isConnected ?? false
  const loadingHistory = props.loadingHistory ?? chatContext?.loadingHistory ?? false
  const loadingPrivateHistory = props.loadingPrivateHistory ?? chatContext?.loadingPrivateHistory ?? false
  const isMaster = props.isMaster ?? chatContext?.isMaster ?? false
  const tableId = props.tableId ?? chatContext?.tableId ?? 'default-table-id'
  const respondingAsNpc = props.respondingAsNpc ?? chatContext?.respondingAsNpc
  const onToggleRespondAsNpc = props.onToggleRespondAsNpc ?? chatContext?.onToggleRespondAsNpc
  const onLiveSync = props.onLiveSync ?? (chatContext?.isMaster ? chatContext?.onLiveSync : undefined)
  const onPoll = props.onPoll ?? (chatContext?.isMaster ? chatContext?.onPoll : undefined)
  const onLab = props.onLab ?? (chatContext?.isMaster ? chatContext?.onLab : undefined)
  // Gating das acoes rapidas: modulos ativados na mesa (do contexto; sem
  // contexto — testes/uso avulso — nenhum modulo aparece)
  const enabledModules = chatContext?.enabledModules ?? EMPTY_MODULES
  // Sessão da missão: só o mestre a comanda, e o painel dele é onde ela vive
  // desde que a MissionSessionBar saiu (2026-07-27).
  const sessaoDaMissao = chatContext?.isMaster ? chatContext?.sessaoDaMissao : null

  // Turn context values
  const permissions = props.permissions ?? turnContext?.permissions
  const currentPlayer = props.currentPlayer !== undefined ? props.currentPlayer : turnContext?.currentPlayer
  // isActive precisa do fallback: o DesktopLayout não passa a prop e sem o
  // contexto o bloqueio de chat por turno nunca ativava no desktop.
  const isActive = props.isActive ?? turnContext?.turnState?.isActive ?? false
  const selectedPrivateUser = props.selectedPrivateUser !== undefined
    ? props.selectedPrivateUser
    : chatContext?.selectedPrivateUser

  // Props que nao vem de contexto
  const {
    disabled,
    isLoading,
    currentUserId,
    characters,
    members
  } = props

  // Fase 11: o texto digitado é estado local — digitar só re-renderiza este
  // componente (e o ChatInput), nunca o GameSession.
  const [groupInput, setGroupInput] = useState('')
  const [privateInput, setPrivateInput] = useState('')

  // Descarta rascunho privado ao trocar de conversa (ou voltar pro grupo) —
  // substitui o setPrivateMessageInput('') que o GameSession fazia.
  const privateConversationKey = chatMode === 'private' ? selectedPrivateUser?.userId ?? null : null
  useEffect(() => {
    setPrivateInput('')
  }, [privateConversationKey])

  const handleSend = useCallback(() => {
    if (chatMode === 'private') {
      // .catch: sem ele uma rejeicao da promise virava unhandled rejection
      // (sem feedback). O onSendPrivateMessage ja mostra toast de erro
      // quando resolve false; aqui so garantimos que reject nao vaza.
      onSendPrivateMessage?.(privateInput)
        .then(ok => {
          if (ok) setPrivateInput('')
        })
        .catch(err => console.error('Erro ao enviar mensagem privada:', err))
    } else {
      if (onSendMessage?.(groupInput)) setGroupInput('')
    }
  }, [chatMode, groupInput, privateInput, onSendMessage, onSendPrivateMessage])

  // Chat privado: sempre com outro participante (anotações têm área própria)
  const isPrivateChat = chatMode === 'private'

  // Selecionar valores corretos baseado no modo
  const inputValue = chatMode === 'private' ? privateInput : groupInput
  const onInputChange = chatMode === 'private' ? setPrivateInput : setGroupInput
  const onSend = handleSend
  const isDisabled = disabled || isLoading || !isConnected || (chatMode === 'private' ? loadingPrivateHistory : loadingHistory)

  return (
    <ChatInput
      value={inputValue}
      onChange={onInputChange}
      onSend={onSend}
      onFocusChange={props.onFocusChange}
      onRollDice={onRollDice}
      onLiveSync={onLiveSync}
      onPoll={onPoll}
      onLab={onLab}
      enabledModules={enabledModules}
      sessaoDaMissao={sessaoDaMissao}
      disabled={isDisabled}
      isPrivateChat={isPrivateChat}
      tableId={tableId}
      onMediaUpload={onMediaUpload}
      permissions={permissions}
      currentPlayer={currentPlayer}
      isActive={isActive}
      isMaster={isMaster}
      characters={characters}
      members={members}
      selectedCharacter={selectedCharacter}
      onSelectCharacter={onSelectCharacter}
      respondingAsNpc={respondingAsNpc}
      controlledNpcs={characters?.filter(c => c.isNpc && c.controlledByUserId === currentUserId) || []}
      onToggleRespondAsNpc={onToggleRespondAsNpc}
    />
  )
}, arePropsEqual)
